import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faFileAlt,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import MemberViewAPI from "../../services/MemberViewAPI";
import reportingAPI from "../../services/ReportingAPI";
import ShieldReportDetail from "../../reports/ShieldReportDetail";
import { formatDateOnly } from "../../utils/DateTimeUtil";
import { useTranslation } from "../../../../i18n/LanguageContext";

/**
 * MemberReportsPanel
 * Renders the selected member user's read-only Shield Reports inline inside the
 * User Management right detail pane.  Fetches reports via the owner-access API so
 * the account owner sees the member's data, not their own.
 *
 * @param {object}   member   - The member user object (must have .uuid)
 * @param {function} onBack   - Callback to return to the Activity tab
 */
function MemberReportsPanel({ member, onBack }) {
  const { t } = useTranslation();
  const memberName = member
    ? `${member.firstName} ${member.lastName}`
    : "Member";

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    if (!member?.uuid) return;
    let cancelled = false;

    const fetchReports = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await MemberViewAPI.getMemberReports(member.uuid);
        if (!cancelled) {
          setReports(response?.reports || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(t("usermgmt.reports.loadFailed"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReports();
    return () => {
      cancelled = true;
    };
  }, [member?.uuid]);

  const getScore = (report) =>
    report?.shieldScoreSummary?.currentScore ??
    report?.shieldScore?.currentScore ??
    report?.shieldScore ??
    null;

  const handleSelectReport = async (reportUuid) => {
    try {
      setDetailLoading(true);
      setDetailError(null);
      const report = await MemberViewAPI.getMemberReportDetail(
        member.uuid,
        reportUuid,
      );
      setSelectedReport(report);
    } catch (err) {
      setDetailError(
        t("usermgmt.reports.detailLoadFailed") ||
          "Failed to load report detail.",
      );
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="um-dashboard-panel">
      {/* Panel header */}
      <div className="um-analyses-panel-header">
        <button
          className="um-analyses-back-btn"
          onClick={selectedReport ? () => setSelectedReport(null) : onBack}
          type="button"
          aria-label={t("usermgmt.analyses.backToActivities")}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {selectedReport
            ? t("usermgmt.reports.backToList") || "Back to Reports"
            : t("usermgmt.analyses.backToActivities")}
        </button>
        <div className="um-analyses-panel-title-row">
          <FontAwesomeIcon
            icon={faFileAlt}
            className="um-analyses-panel-icon"
          />
          <h3 className="um-analyses-panel-title">
            {t("usermgmt.reports.panelTitle").replace("{name}", memberName)}
          </h3>
          <span className="um-analyses-readonly-badge">
            {t("usermgmt.analyses.readOnly")}
          </span>
        </div>
      </div>

      {/* Scrollable reports content */}
      <div className="um-dashboard-panel-body">
        {/* ── Detail view ── */}
        {detailLoading && (
          <div className="um-analyses-loading">
            <FontAwesomeIcon icon={faSpinner} spin />
            <span>{t("usermgmt.reports.loading")}</span>
          </div>
        )}

        {!detailLoading && detailError && (
          <div className="um-analyses-error">{detailError}</div>
        )}

        {!detailLoading && !detailError && selectedReport && (
          <ShieldReportDetail
            initialReport={selectedReport}
            onBack={() => setSelectedReport(null)}
          />
        )}

        {/* ── List view ── */}
        {!detailLoading && !detailError && !selectedReport && (
          <>
            {loading && (
              <div className="um-analyses-loading">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>{t("usermgmt.reports.loading")}</span>
              </div>
            )}

            {!loading && error && (
              <div className="um-analyses-error">{error}</div>
            )}

            {!loading && !error && reports.length === 0 && (
              <div className="um-analyses-empty">
                {t("usermgmt.reports.empty").replace("{name}", memberName)}
              </div>
            )}

            {!loading && !error && reports.length > 0 && (
              <div className="shield-reports-list">
                {/* Table header */}
                <div className="report-row report-header-row">
                  <span className="report-col col-type">
                    {t("usermgmt.reports.colType")}
                  </span>
                  <span className="report-col col-period">
                    {t("usermgmt.reports.colPeriod")}
                  </span>
                  <span className="report-col col-score">
                    {t("usermgmt.reports.colScore")}
                  </span>
                  <span className="report-col col-date">
                    {t("usermgmt.reports.colCreated")}
                  </span>
                </div>
                {reports.map((report) => {
                  const score = getScore(report);
                  const uuid = report.reportUuid || report.reportId;
                  return (
                    <div
                      key={uuid}
                      className="report-row report-row-clickable"
                      onClick={() => handleSelectReport(uuid)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleSelectReport(uuid)
                      }
                    >
                      <span className="report-col col-type">
                        <span className="report-type-badge">
                          {report.reportType === "PERSONAL_SHIELD"
                            ? t("usermgmt.reports.typePersonal")
                            : report.reportType}
                        </span>
                      </span>
                      <span className="report-col col-period">
                        {reportingAPI.formatPeriodLabel(
                          report.periodType,
                          report.periodStart || report.periodStartDate,
                          report.periodEnd || report.periodEndDate,
                        )}
                      </span>
                      <span
                        className="report-col col-score"
                        style={{
                          color:
                            score != null
                              ? reportingAPI.getScoreColor(score)
                              : undefined,
                        }}
                      >
                        {score != null ? <strong>{score}</strong> : "—"}
                      </span>
                      <span className="report-col col-date">
                        {formatDateOnly(report.generatedAt || report.createdAt)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default MemberReportsPanel;
